import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import FilterRow from "./FilterRow";
import MediaCard from "@/components/feature/MediaCard";
import type { MediaItem, MediaType } from "@/types/media";
import {
  defaultFilters,
  filterMedia,
  genreOptions,
  regionOptions,
  yearOptions,
  type CategoryFilters,
} from "@/utils/mediaData";

interface CategoryViewProps {
  type: MediaType;
  items: MediaItem[];
  title: string;
  description: string;
  bannerImage: string;
  breadcrumb: string;
}

const PAGE_SIZE = 8;
const ratingOptions = ["9", "8", "7"];
const sortOptions = [
  { key: "latest", label: "最新上映", icon: "ri-time-line" },
  { key: "rating", label: "评分最高", icon: "ri-star-line" },
  { key: "views", label: "热度最高", icon: "ri-fire-line" },
];

export default function CategoryView({
  title,
  description,
  bannerImage,
  breadcrumb,
  items,
}: CategoryViewProps) {
  const [filters, setFilters] = useState<CategoryFilters>({ ...defaultFilters });
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const filtered = useMemo(() => filterMedia(items, filters), [items, filters]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const update = (key: keyof CategoryFilters) => (value: string) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="w-full">
      <section className="relative w-full h-[200px] md:h-[260px] overflow-hidden">
        <img
          src={bannerImage}
          alt={`${title}频道`}
          title={`${title} - 91n影视大全`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/45 to-black/10"></div>
        <div className="relative z-10 w-full h-full px-4 md:px-8 flex flex-col justify-center">
          <nav className="flex items-center gap-1.5 text-xs md:text-sm text-white/70 mb-3">
            <Link to="/" className="hover:text-white transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">{breadcrumb}</span>
          </nav>
          <h1 className="font-heading font-black text-white text-2xl md:text-4xl mb-2">{title}</h1>
          <p className="text-white/80 text-sm md:text-base max-w-2xl">{description}</p>
        </div>
      </section>

      <div className="w-full px-4 md:px-8 py-6 md:py-8">
        <div className="rounded-lg bg-background-100 border border-background-200 p-4 md:p-5">
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            className="sm:hidden w-full flex items-center justify-between text-sm font-semibold text-foreground-800 mb-2"
          >
            <span className="flex items-center gap-2">
              <i className="ri-filter-3-line text-primary-500"></i>
              筛选条件
            </span>
            <i className={filtersOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
          </button>
          <div className={`${filtersOpen ? "block" : "hidden"} sm:block`}>
            <FilterRow label="类型" options={genreOptions} value={filters.genre} onChange={update("genre")} />
            <FilterRow label="地区" options={regionOptions} value={filters.region} onChange={update("region")} />
            <FilterRow label="年份" options={yearOptions} value={filters.year} onChange={update("year")} />
            <FilterRow
              label="评分"
              options={ratingOptions.map((r) => `${r}分以上`)}
              value={filters.rating === "全部" ? "全部" : `${filters.rating}分以上`}
              onChange={(v) => update("rating")(v === "全部" ? "全部" : v.replace("分以上", ""))}
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-5 mb-5">
          <div className="flex items-center gap-2">
            {sortOptions.map((option) => {
              const active = filters.sort === option.key;
              return (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => update("sort")(option.key)}
                  className={`h-9 px-3.5 rounded-md text-xs md:text-sm font-medium flex items-center gap-1.5 whitespace-nowrap transition-colors ${
                    active
                      ? "bg-accent-500 text-background-50"
                      : "bg-background-100 text-foreground-600 border border-background-200 hover:text-accent-600"
                  }`}
                >
                  <i className={option.icon}></i>
                  {option.label}
                </button>
              );
            })}
          </div>
          <p className="text-xs md:text-sm text-foreground-500">
            共找到 <span className="text-primary-500 font-semibold">{filtered.length}</span> 部作品
          </p>
        </div>

        {paged.length > 0 ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {paged.map((item) => (
                <MediaCard key={item.id} item={item} showUpdated={item.type === "tv"} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <button
                  type="button"
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="h-9 px-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-600 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-300 transition-colors flex items-center gap-1"
                >
                  <i className="ri-arrow-left-s-line"></i>
                  上一页
                </button>
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setPage(i + 1)}
                    className={`w-9 h-9 rounded-md text-sm font-medium transition-colors ${
                      page === i + 1
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-50 border border-background-200 text-foreground-600 hover:border-primary-300"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="h-9 px-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-600 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-300 transition-colors flex items-center gap-1"
                >
                  下一页
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="py-20 flex flex-col items-center justify-center text-center">
            <span className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </span>
            <p className="text-foreground-800 font-semibold mb-1">未找到相关影视内容</p>
            <p className="text-sm text-foreground-500 mb-5">可尝试更换筛选条件，或浏览其他分类</p>
            <button
              type="button"
              onClick={() => setFilters({ ...defaultFilters })}
              className="h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold whitespace-nowrap transition-colors"
            >
              重置筛选条件
            </button>
          </div>
        )}
      </div>
    </div>
  );
}